import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import apiService from '../services/api';
import { featuredDraws, formatCompactCountdown, formatDisplayNumber, openBonusDrops, shouldShowDream } from '../utils/dreamSummary';
import { ReactComponent as TicketIcon } from '../assets/icons/ticket-entries.svg';
import { BoostChip, BoostTile, useBoostClock } from './DreamBoostBits';
import '../styles/DreamFeaturedDraws.css';

function endMs(prize) {
  const ms = prize.dream_ends_at ? new Date(prize.dream_ends_at).getTime() : NaN;
  return Number.isFinite(ms) ? ms : null;
}

// The prize draw opens the Dream page; a bonus drop opens its own view of it,
// as the embed's #/dream and #/dream/drops/<id>.
function DrawCard({ prize, label, odd, to }) {
  const left = formatCompactCountdown(endMs(prize), Date.now(), 'Ended');
  return (
    <Link
      to={to}
      className={`dream-card${odd ? ' is-odd-last' : ''}`}
      aria-label={`${label}: ${prize.title}`}
    >
      {prize.cover_image_url ? <img src={prize.cover_image_url} alt="" /> : <span className="dream-card-ph" />}
      <span className="dream-card-label">{label}</span>
      <span className="dream-card-body">
        <strong className="dream-card-title">{prize.title}</strong>
        {left ? <span className="dream-card-left">{left}</span> : null}
      </span>
    </Link>
  );
}

// Reads the Dream summary the embed reads. Nothing renders until it arrives,
// when the school's program is off, or when there is nothing to show.
// `source="prizes"` is the Rewards page: the hero is prizes[0], as in the embed
// and the app. Home shows the active draw from featured_draw.
function DreamFeaturedDraws({ source = 'featured' }) {
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    let cancelled = false;
    apiService
      .getDreamSummary()
      .then((data) => {
        if (!cancelled) setSummary(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const now = useBoostClock(summary);
  const featured =
    source === 'prizes'
      ? shouldShowDream(summary) && (summary.prizes || [])[0]
        ? [summary.prizes[0]]
        : []
      : featuredDraws(summary);
  const drops = openBonusDrops(summary);
  if (!featured.length && !drops.length) return null;
  const entries = Number(summary.entries) || 0;
  return (
    <div className="dream-featured">
      {featured.length && source !== 'prizes' ? (
        // Home's "Win a prize" block (Figma 6308:37303): the draw card, then the
        // fan's entries with the boost chip and tile, as on the embed home.
        <section className="dream-win-block" aria-label="Win a prize">
          <div className="dream-win-block-head">
            <h2>Win a prize</h2>
            <Link to="/dream">View details</Link>
          </div>
          <div className="dream-win-block-card">
            <div className="dream-featured-column">
              {featured.map((prize) => (
                <DrawCard key={prize.id} prize={prize} label="Prize draw" to="/dream" />
              ))}
            </div>
            <div className="dream-win-block-entries">
              <div className="dream-win-block-label">
                <span className="dream-win-block-label-main">
                  <TicketIcon aria-hidden="true" />
                  Your entries in this draw
                </span>
                <BoostChip summary={summary} now={now} />
              </div>
              <div className="dream-win-block-num">{formatDisplayNumber(entries)}</div>
              <BoostTile summary={summary} now={now} />
              <p className="dream-win-block-copy">
                For every purchase you receive entries which increase your chance to win a big prize.
              </p>
            </div>
          </div>
        </section>
      ) : featured.length ? (
        <div className="dream-featured-column">
          {featured.map((prize) => (
            <DrawCard key={prize.id} prize={prize} label="Prize draw" to="/dream" />
          ))}
        </div>
      ) : null}
      {drops.length ? (
        <div className="dream-drops-grid">
          {drops.map((prize, index) => (
            <DrawCard
              key={prize.id}
              prize={prize}
              label="Bonus drop"
              to={`/dream/drops/${encodeURIComponent(prize.id)}`}
              odd={drops.length % 2 === 1 && index === drops.length - 1}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default DreamFeaturedDraws;
